import {
  getErrorMessage,
  getHttpStatus,
  logFeatureEvent,
  reportFeatureIssue,
  type ObservabilityScope,
} from "./shared";

type ConnectorAction =
  | "oauth_start"
  | "oauth_callback"
  | "connect"
  | "disconnect"
  | "save_settings"
  | "load_connections"
  | "health_check";

type ConnectorAttributes = {
  connectorId?: string | null;
  connectionId?: string | null;
  category?: string | null;
};

const attributesFor = ({ connectorId, connectionId, category }: ConnectorAttributes = {}) => ({
  connector_id: connectorId ?? null,
  connection_id: connectionId ?? null,
  connector_category: category ?? null,
});

export const logConnectorEvent = (
  event:
    | "oauth_started"
    | "connector_connected"
    | "connector_disconnected"
    | "settings_saved"
    | "health_checked",
  scope: ObservabilityScope,
  attributes?: ConnectorAttributes,
) =>
  logFeatureEvent({
    feature: "connectors",
    event,
    scope,
    attributes: attributesFor(attributes),
  });

/**
 * A user closing the OAuth window, or a provider marking a connection stale,
 * is an expected outcome — Log it so the team can see connect-rate per
 * property without a stream of Issues.
 */
export const logConnectorDegradation = (
  event: "oauth_abandoned" | "connection_unhealthy" | "connections_unavailable",
  scope: ObservabilityScope,
  attributes?: ConnectorAttributes & { reason?: string },
) =>
  logFeatureEvent({
    feature: "connectors",
    event,
    level: "warn",
    scope,
    attributes: {
      reason: attributes?.reason ?? null,
      ...attributesFor(attributes),
    },
  });

// getRequest throws a bare Error("HTTP error! status: 403") with no `.status`
// field, so getHttpStatus misses it and the message is the only signal.
const ACCESS_DENIED_MESSAGE =
  /\b(?:HTTP(?: error! status:)?\s*(?:401|403)|access denied|forbidden|unauthori[sz]ed)\b/i;

export const isConnectorAccessDenied = (error: unknown) => {
  const status = getHttpStatus(error);
  if (status === 401 || status === 403) return true;

  return ACCESS_DENIED_MESSAGE.test(getErrorMessage(error));
};

export const reportConnectorFailure = (
  action: ConnectorAction,
  error: unknown,
  scope: ObservabilityScope,
  attributes?: ConnectorAttributes,
) => {
  const httpStatus = getHttpStatus(error);

  if (isConnectorAccessDenied(error)) {
    logFeatureEvent({
      feature: "connectors",
      event: "access_denied",
      level: "warn",
      message: `Connectors: ${action} access denied`,
      scope,
      attributes: {
        ...attributesFor(attributes),
        http_status: httpStatus,
      },
      dedupKey: `access-denied:${action}:${scope.propertyId ?? "unknown"}`,
    });
    return;
  }

  reportFeatureIssue({
    feature: "connectors",
    error,
    action,
    message: `Connectors: ${action} failed`,
    scope,
    context: attributesFor(attributes),
  });
};
